import { useCallback, useRef, useEffect } from "react";
import type { AxiosRequestConfig, AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/api/client";

function getResponseStatus(err: unknown): number | undefined {
  return (err as AxiosError)?.response?.status;
}

function isOnAuthPage() {
  const path = window.location.pathname;
  return path.startsWith("/login") || path.startsWith("/auth/");
}

/**
 * Returns a stable `request` function that:
 * - Injects the current Firebase Bearer token (cached to avoid repeated async calls)
 * - On 401: force-refreshes the token and retries once
 * - Redirects to /login only if retry still fails or there is no Firebase user
 *
 * Use inside React Query queryFn / mutationFn — errors are rethrown so React
 * Query can surface them to the global error handler.
 */
export function useBackendAuthenticated() {
  const { firebaseUser } = useAuth();
  const navigate = useNavigate();
  const cachedTokenRef = useRef<string | null>(null);

  // Pre-fetch and cache the token whenever the Firebase user changes.
  // Firebase SDK caches the token internally (~55 min), but getIdToken() is
  // still async. By eagerly resolving it once we avoid awaiting on every request.
  useEffect(() => {
    if (firebaseUser) {
      firebaseUser.getIdToken().then((t) => { cachedTokenRef.current = t; });
    } else {
      cachedTokenRef.current = null;
    }
  }, [firebaseUser]);

  return useCallback(
    async <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
      const makeRequest = async (token: string | null) =>
        apiClient.request<T>({
          ...config,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...config.headers,
          },
        });

      // Use the cached token synchronously when available; fall back to async
      const token = cachedTokenRef.current
        ?? (firebaseUser ? await firebaseUser.getIdToken() : null);

      try {
        const res = await makeRequest(token);
        return res.data;
      } catch (err: unknown) {
        if (getResponseStatus(err) === 401) {
          // Try a forced token refresh before giving up
          if (firebaseUser) {
            try {
              const freshToken = await firebaseUser.getIdToken(true);
              cachedTokenRef.current = freshToken;
              const res = await makeRequest(freshToken);
              return res.data;
            } catch (retryErr: unknown) {
              if (getResponseStatus(retryErr) === 401 && !isOnAuthPage()) {
                navigate("/login");
              }
              throw retryErr;
            }
          }

          if (!isOnAuthPage()) navigate("/login");
        }

        throw err;
      }
    },
    [firebaseUser, navigate],
  );
}
